import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const UserTenants = ({ userId = null }) => {
    const { token } = useAuth();
    const [tenants, setTenants] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [newTenant, setNewTenant] = useState({ tenant_id: '', receive_emails: true });
    const [availableTenants, setAvailableTenants] = useState([]);

    // Fetch user's current tenant assignments
    const fetchUserTenants = async () => {
        try {
            const path = userId ? `/api/users/${userId}/tenants` : '/api/auth/me/tenants';
            const { data } = await axios.get(path);
            setTenants(data);
        } catch (err) {
            setError('Error fetching tenant assignments');
        } finally {
            setLoading(false);
        }
    };

    // Fetch available tenants
    const fetchAvailableTenants = async () => {
        try {
            const path = userId ? '/api/users/tenants' : '/api/gold/tenants';
            const { data } = await axios.get(path);
            setAvailableTenants(data);
        } catch (err) {
            console.error('Error fetching available tenants:', err);
        }
    };

    useEffect(() => {
        fetchUserTenants();
        fetchAvailableTenants();
    }, [token]);

    // Add new tenant assignment
    const addTenant = async (e) => {
        e.preventDefault();
        if (!userId) return;
        try {
            const path = `/api/users/${userId}/tenants`;
            const { data } = await axios.post(path, newTenant);
            setTenants([...tenants, data]);
            setNewTenant({ tenant_id: '', receive_emails: true });
        } catch (err) {
            const detail = err.response?.data?.detail;
            const message = typeof detail === 'string' ? detail : (detail ? JSON.stringify(detail) : 'Error adding tenant');
            setError(message);
        }
    };

    // Update tenant email preferences
    const updateTenant = async (tenantId, updates) => {
        try {
            const path = userId ? `/api/users/${userId}/tenants/${tenantId}` : `/api/auth/me/tenants/${tenantId}`;
            const method = userId ? 'PATCH' : 'PUT';
            const { data } = await axios({ url: path, method, data: updates });
            setTenants(tenants.map(t => 
                t.tenant_id === tenantId ? data : t
            ));
        } catch (err) {
            const detail = err.response?.data?.detail;
            const message = typeof detail === 'string' ? detail : (detail ? JSON.stringify(detail) : 'Error updating tenant preferences');
            setError(message);
        }
    };

    // Remove tenant assignment (admin only)
    const removeTenant = async (tenantId) => {
        if (!userId) return;
        if (!window.confirm('Are you sure you want to remove this tenant assignment?')) {
            return;
        }
        
        try {
            const path = `/api/users/${userId}/tenants/${tenantId}`;
            await axios.delete(path);
            setTenants(tenants.filter(t => t.tenant_id !== tenantId));
        } catch (err) {
            const detail = err.response?.data?.detail;
            const message = typeof detail === 'string' ? detail : (detail ? JSON.stringify(detail) : 'Error removing tenant assignment');
            setError(message);
        }
    };

    const getTenantName = (tenantId) => {
        const t = availableTenants.find((x) => x.tenant_id === tenantId || x.id === tenantId);
        if (!t) return tenantId;
        // Prefer common fields for display name
        return t.location || t.name || t.code || tenantId;
    };

    if (loading) {
        return <div className="flex justify-center items-center p-8">Loading...</div>;
    }

    return (
        <div className="max-w-4xl mx-auto p-4">
            
            {error && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                    {error}
                </div>
            )}

            {/* Add tenant: admin manages other users; self-service assignment is disabled */}
            {userId && (
            <div className="bg-white rounded-lg shadow p-6 mb-6">
                <form onSubmit={addTenant} className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Select tenant
                        </label>
                            <select
                                value={newTenant.tenant_id}
                                onChange={(e) => setNewTenant({ ...newTenant, tenant_id: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                required
                            >
                                <option value="">Select tenant</option>
                                {availableTenants.map((t) => (
                                    <option key={t.tenant_id} value={t.tenant_id}>
                                        {t.location}
                                    </option>
                                ))}
                            </select>
                    </div>
                    <button
                        type="submit"
                        className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                        Add Tenant
                    </button>
                </form>
            </div>
            )}
            {!userId && (
                <p className="text-sm text-gray-600 mb-4">
                    Tenant access is assigned by an administrator. Contact an admin to change your locations.
                </p>
            )}

            {/* Current Tenant Assignments */}
            <div className="bg-white rounded-lg shadow">
                <div className="divide-y divide-gray-200">
                    {tenants.length === 0 ? (
                        <div className="px-6 py-4 text-gray-500">
                            No tenant assignments found.
                        </div>
                    ) : (
                        tenants.map((tenant) => (
                            <div key={tenant.id} className="px-6 py-4">
                                <div className="flex items-center justify-between">
                                    <div className="flex-1">
                                        <h3 className="text-lg font-medium text-gray-900">
                                            {getTenantName(tenant.tenant_id)}
                                        </h3>
                                        <p className="text-sm text-gray-500">
                                            Email notifications: {tenant.receive_emails ? 'Enabled' : 'Disabled'}
                                        </p>
                                    </div>
                                    {userId && (
                                    <div className="flex items-center">
                                        <button
                                            onClick={() => removeTenant(tenant.tenant_id)}
                                            className="text-red-600 hover:text-red-800 text-sm font-medium"
                                        >
                                            Remove
                                        </button>
                                    </div>
                                    )}
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>
        </div>
    );
};

export default UserTenants; 